import { useMemo, useReducer, useRef } from "react";

import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Select,
  Modal
} from "@theorgchartdev/storybook";
import { SelectableSearchMenu } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { appstr } from "@coreapp/utils/appstr";
import { ListItemOption } from "@theorgchartdev/storybook/dist/components/List/ListItem";
import LevelHandler from "@/Utils/Chart/LevelHandler";
import { BoxChip } from "@/Utils/BoxChip";
import { BuilderModalProps } from "@/Utils/Chart/NewDocForm";
import {
  customChartFormReducer,
  CustomChartFormState,
  getInitialState
} from "@/Utils/CustomChart/NewCustomChartForm";
import { ChartBuilderRuleInput } from "../../ChartBuilderModal/ChartBuilderRuleInput/ChartBuilderRuleInput";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { convertStdDateString } from "@coreapp/utils/convertStdDateString";
import {
  enumObjectTypes,
  kDefaultEmployeeHeadcountView,
  kDefaultPositionBasicView,
  kNameID,
  kPersonID,
  kPositionID,
  kPositionTitleID,
  kTitleID
} from "@coreapp/constants";
import { PitBuilderInfo } from "./PointInTimeBuilderModal.utils";

const PiTBuilderModal = (
  props: BuilderModalProps<CustomChartFormState> & {
    pitBuilderInfo: PitBuilderInfo;
    isCreating?: boolean;
  }
) => {
  const { show, onClose, onCreate } = props;
  const initialFilterRuleRef = useRef(null);
  const isPosition = IsPositionChart(props.pitBuilderInfo);
  const chartID = props.pitBuilderInfo?.ID;
  const recordIDName = props.pitBuilderInfo?.fields?.[isPosition ? kPositionID : kPersonID];

  const searchMapping = {
    Name: props.pitBuilderInfo?.fields?.[kNameID],
    Title: props.pitBuilderInfo?.fields?.[isPosition ? kPositionTitleID : kTitleID],
    EntityID: recordIDName
  };
  const chartData = {
    ID: chartID,
    customFields: isPosition
      ? {
          [enumObjectTypes.position]: props.pitBuilderInfo?.positionFields,
          [enumObjectTypes.holder]: props.pitBuilderInfo?.holderFields
        }
      : props.pitBuilderInfo?.headers,
    isArchive: true
  };

  // Form state
  const [formState, dispatch] = useReducer(customChartFormReducer, getInitialState());

  const handleClose = () => {
    if (props.isCreating) {
      return;
    }
    dispatch({ type: "SET_FILTER", payload: initialFilterRuleRef.current });
    onClose();
  };

  const handleCreate = () => {
    storeCurrentFilter();
    onCreate(GetBuilderInfo());
  };

  const clearStoredFilter = () => {
    dispatch({ type: "SET_FILTER", payload: null });
    initialFilterRuleRef.current = null;
  };

  const storeCurrentFilter = () => {
    initialFilterRuleRef.current = formState.filter?.Clone();
  };

  const GetBuilderInfo = () => {
    let activeViewID = GetDoc()?.GetActiveTemplate()?.GetTemplateID();
    if (GetDoc()?.IsPositionDocument() !== isPosition) {
      // We can't use the same View! Opening PM chart from a non-PM chart and vice versa
      activeViewID =
        props.pitBuilderInfo?.views?.[0] ??
        (isPosition ? kDefaultPositionBasicView : kDefaultEmployeeHeadcountView);
    }

    const builderInfo: any = {
      liveChart: false,
      levels: formState.levels,
      topBoxID: formState.topbox?.topBoxID,
      importFilter: { rule: formState.filter },
      viewid: activeViewID,
      fBuildArchive: true,
      doctype: "Archive"
    };

    return builderInfo;
  };

  const baseClass = "ocn-builder";
  const levelOptions = LevelHandler.buildLevelOptions((level: ListItemOption) =>
    dispatch({ type: "SET_LEVELS", payload: level.value })
  );

  const requiredTicker = <span className={"ocn-form-label-required"}> *</span>;

  const headerStr = useMemo(() => {
    const wsMgr = GetWSMgr();
    const oi = wsMgr?.GetOrgInfo();
    const DBHotDate = wsMgr?.GetPointInTime();
    const splitResult = DBHotDate?.["hotdate"]?.split(":");
    if (!splitResult) return `${appstr("Chart Builder")} - ${appstr("Snapshot")}`;

    const dateObj: any = convertStdDateString(splitResult[0], splitResult[1] ?? null);
    const dateLabel = dateObj.FormatDate(oi?.GetDateFormat());
    return `${appstr("Chart Builder")} - ${appstr("Snapshot")} | ${dateLabel}`;
  }, [props.pitBuilderInfo]);

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="builder-card"
        show={show}
        centerScreen={true}
        onClose={handleClose}
        fullWidth={true}
        customStyle={{ padding: 0, width: "600px" }}
        className="ocn-custom-chart-builder-modal ocn-chart-doc-builder-modal"
      >
        <DialogTitle divider={true} onClose={handleClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"planning"} />
            {headerStr}
          </span>
        </DialogTitle>

        {/* CC TOPBOX */}
        <DialogContent dividers={false}>
          <div className="ocn-builder">
            <label className="ocn-form-label ocn-form-input-label-vertical-gap">
              {appstr(isPosition ? "Build Position Doc" : "Build Employees Chart", {
                document: appstr("Custom Chart").toLowerCase()
              })}
              {requiredTicker}
            </label>
            <div className={"ocn-builder-search"}>
              <SelectableSearchMenu
                dataTestId="custom-chart-topbox-search"
                inputSize="medium"
                inputProps={{
                  "aria-label": "Which employee would you like at the top of this chart?"
                }}
                listWidth="fill"
                displayRecents={false}
                selectedItem={formState.topbox}
                onRemoveItem={() => dispatch({ type: "SET_TOPBOX", payload: null })}
                searchMenuProps={{
                  clearOnSelect: true,
                  clearable: true,
                  placeholder: formState.topbox ? "" : appstr("Search by name or title")
                }}
                handleSearchOptionClick={(target: any) => {
                  const lookupRecordObj = BoxChip.FromMappedObject(
                    target.result,
                    searchMapping,
                    isPosition
                  );
                  dispatch({ type: "SET_TOPBOX", payload: lookupRecordObj, isPosition });
                }}
                queryOptions={{ includeAux: false }}
                queryChartId={chartID}
                isPositionChart={isPosition}
                recIDFieldName={recordIDName}
                isArchive={true}
                mapping={searchMapping}
              />
            </div>
          </div>
        </DialogContent>

        {/* CC LEVELS */}
        <DialogContent dividers={false}>
          <span className={baseClass}>
            <label className="ocn-form-label ocn-form-input-label-vertical-gap">
              {appstr(isPosition ? "Select Build Position Level" : "Select Build Chart Level")}
              {requiredTicker}
            </label>
            <div className={"ocn-builder-search"}>
              <span className={"ocn-builder-edit-text"}>
                <Select
                  dataTestId={"builder-lvls-menu"}
                  optionSize="medium"
                  selectSize="medium"
                  value={formState.levels}
                  options={levelOptions}
                  inputProps={{
                    "aria-label": "How many levels under this employee would you like to include?"
                  }}
                />
              </span>
            </div>
          </span>
        </DialogContent>

        {/* CC FILTER */}
        <DialogContent dividers={true}>
          <span className={baseClass}>
            <label className="ocn-form-label">{appstr("Chart Builder Filter")}</label>
            <div className="ocn-section-prompt-text-description ocn-form-input-label-vertical-gap">
              {appstr("Chart Builder Filter Directions")}
            </div>
            <div className={"ocn-builder-edit"}>
              <span id={"builder-filter-btn"} className={"ocn-builder-edit-text"}>
                <ChartBuilderRuleInput
                  hasError={false}
                  onSave={storeCurrentFilter}
                  onDelete={clearStoredFilter}
                  rule={formState.filter}
                  setRule={(value: any) => dispatch({ type: "SET_FILTER", payload: value })}
                  chartData={chartData}
                />
              </span>
            </div>
          </span>
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions>
          <DialogActions.Tertiary
            onClick={handleClose}
            size="medium"
            disabled={props.isCreating}
            dataTestId="chart-mode-explorer-pit-builder-cancel-btn"
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={handleCreate}
            dataTestId={"builder-create-archive-btn"}
            size="medium"
            loading={props.isCreating}
            disabled={props.isCreating || !formState.isDirty}
          >
            {appstr("Create")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default PiTBuilderModal;
